import { Fingerprint, Info, Shield } from 'lucide-react';
import React, { useState } from 'react';
import PhoneInput from 'react-phone-number-input';

import { Button } from '@docuid/ui/components/ui/button';

import 'react-phone-number-input/style.css';

import { ThemeService, type ThemeOption } from '@/taskpane/services/ThemeService';

import DownloadSheet from './DownloadSheet';
import GetStartedSheet from './GetStartedSheet';

interface LoginFormProperties {
  onLogin: (phoneNumber: string) => Promise<void>;
  isLoading: boolean;
}

const LoginForm: React.FC<LoginFormProperties> = ({ onLogin, isLoading }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isDownloadSheetOpen, setIsDownloadSheetOpen] = useState(false);
  const [isGetStartedOpen, setIsGetStartedOpen] = useState(false);
  const [themeState, setThemeState] = useState<ThemeOption>(ThemeService.getResolvedTheme());

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (phoneNumber && phoneNumber.length >= 10) {
      void onLogin(phoneNumber);
    }
  };

  const isValidPhone = phoneNumber.length >= 10;

  return (
    <div className="relative flex min-h-screen items-center justify-center p-6">
      {/* Theme Switcher in Top Right */}
      <div className="bg-muted/60 border-border/40 absolute top-4 right-4 z-20 flex rounded-lg border p-0.5 backdrop-blur-md select-none">
        {(['light', 'dark', 'system'] as const).map((option) => (
          <button
            key={option}
            onClick={() => {
              ThemeService.setTheme(option);
              setThemeState(option);
            }}
            className={`cursor-pointer rounded-md border-none px-2 py-1 text-[10px] font-semibold capitalize transition-all ${
              themeState === option
                ? 'bg-card text-[var(--accent)] shadow-sm'
                : 'text-muted-foreground hover:text-foreground bg-transparent'
            }`}
            type="button"
          >
            {option}
          </button>
        ))}
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex justify-center rounded-2xl p-2 backdrop-blur-md select-none">
          <img
            src="assets/logo-transparent-bg.png"
            alt="iVALT DocuID"
            className="w-[160px] object-contain dark:brightness-0 dark:invert"
          />
        </div>
        <div className="bg-card/85 border-border/40 flex w-full flex-col gap-5 rounded-2xl border p-6 shadow-2xl backdrop-blur-md">
          {/* Header Section */}
          <div className="flex flex-col items-center gap-3">
            <p className="text-muted-foreground px-1 text-center text-xs leading-relaxed">
              Secure your digital assets with advanced client-side file encryption powered by iVALT
              biometrics.
            </p>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              <div>
                <div className="mb-2 flex items-center justify-between select-none">
                  <label htmlFor="phone" className="text-foreground text-xs font-semibold">
                    Mobile Number
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsGetStartedOpen(true);
                    }}
                    className="group/tip relative flex h-5.5 w-5.5 cursor-pointer items-center justify-center rounded-full border border-[var(--accent)]/20 bg-[var(--accent-dim)] text-[var(--accent)] transition-colors hover:bg-[var(--accent)] hover:text-white"
                  >
                    <span className="bg-popover text-popover-foreground border-border pointer-events-none absolute -top-7 left-1/2 z-50 -translate-x-1/2 rounded-md border px-2 py-1 text-[10px] font-medium whitespace-nowrap opacity-0 shadow-md transition-opacity group-hover/tip:opacity-100">
                      How to get started
                    </span>
                    <Info size={12} />
                  </button>
                </div>
                <PhoneInput
                  id="phone"
                  international
                  defaultCountry="IN"
                  value={phoneNumber}
                  onChange={(value) => {
                    setPhoneNumber(value ?? '');
                  }}
                  placeholder="Enter mobile number"
                  disabled={isLoading}
                  className="border-border bg-card/50 text-foreground w-full rounded-xl border px-3 py-2 text-sm focus:border-transparent focus:ring-1 focus:ring-[var(--accent)] focus:outline-none [&_.PhoneInputCountry]:mr-2 [&_.PhoneInputCountrySelect]:cursor-pointer [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none"
                />
              </div>

              <Button
                type="submit"
                disabled={!isValidPhone || isLoading}
                className="flex h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-[var(--accent)] text-xs font-semibold text-white transition-all hover:bg-[var(--accent-dark)] hover:opacity-90 active:scale-[0.98]"
              >
                <Fingerprint size={15} />
                <span>{isLoading ? 'Authenticating...' : 'Login with Biometrics'}</span>
              </Button>

              <div className="bg-muted/40 border-border/40 text-muted-foreground flex items-start gap-2.5 rounded-xl border p-3 text-[10px] leading-relaxed select-none">
                <Shield size={14} className="mt-0.5 shrink-0 text-[var(--accent)]" />
                <span>
                  Zero-knowledge client-side encryption keys are generated locally. Your biometrics
                  stay private.
                </span>
              </div>

              <div className="flex justify-center select-none">
                <p className="text-muted-foreground text-xs">
                  New to DocuID?{' '}
                  <a
                    href="https://www.docuid.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-[#f54e00] no-underline hover:opacity-85"
                  >
                    Create an account
                  </a>
                </p>
              </div>
            </div>
          </form>

          <div className="border-border/40 flex justify-center border-t pt-4 select-none">
            <p className="text-muted-foreground text-center text-[11px]">
              Need the iVALT mobile app?{' '}
              <button
                onClick={() => {
                  setIsDownloadSheetOpen(true);
                }}
                className="cursor-pointer border-none bg-none p-0 text-[11px] font-semibold text-[#f54e00] no-underline hover:opacity-85"
                type="button"
              >
                Download here
              </button>
            </p>
          </div>
        </div>
      </div>

      <DownloadSheet
        isOpen={isDownloadSheetOpen}
        onClose={() => {
          setIsDownloadSheetOpen(false);
        }}
      />
      <GetStartedSheet
        isOpen={isGetStartedOpen}
        onClose={() => {
          setIsGetStartedOpen(false);
        }}
        onOpenDownload={() => {
          setIsDownloadSheetOpen(true);
        }}
      />
    </div>
  );
};

export default LoginForm;
